import React, { useMemo } from "react";
import {
  DataTable,
  Button,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Station } from "../dialogs/ViewStationDialog";
import { getStationById, toggleStationVersion } from "../../api";
import { CircleChevronLeft, Trash2, Eye, Edit, Power, Plus } from "lucide-react";

interface StationDetailsPanelProps {
  station: Station;
  onViewStation: (station: Station) => void;
  onDeleteStation: (station: Station) => void;
  onCreateVersion?: (parent: Station, activeVersion?: Station) => void;
  onStationsUpdated?: () => void;
  onBack: () => void;
  versionsLoading?: boolean;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const StationDetailsPanel: React.FC<StationDetailsPanelProps> = ({
  station,
  onViewStation,
  onDeleteStation,
  onCreateVersion,
  onStationsUpdated,
  onBack,
  versionsLoading = false,
  hasEditAccess = true,
  hasManageAccess = true,
}) => {
  // Helper function to find the active version (either parent or child)
  const findActiveVersion = (station: Station): Station => {
    if (station.is_active) {
      return station;
    }
    if (station.children && station.children.length > 0) {
      const activeChild = station.children.find((child) => child.is_active);
      if (activeChild) return activeChild;
    }
    return station;
  };

  const getAllVersions = (station: Station): Station[] => {
    const versions = [station];
    if (station.children && station.children.length > 0) {
      versions.push(...station.children);
    }
    return versions.sort(
      (a, b) => parseInt((b.version || "0").toString()) - parseInt((a.version || "0").toString())
    );
  };

  const versions = useMemo(() => getAllVersions(station), [station]);

  const handleCreateVersion = () => {
    if (!onCreateVersion) return;
    const activeVersion = findActiveVersion(station);
    onCreateVersion(station, activeVersion);
  };

  const handleEditAsNewVersion = async (versionStation: Station) => {
    if (!onCreateVersion) return;
    const id = versionStation.buddy_id;
    try {
      const full = (await getStationById(id || "")) as unknown as Station;
      onCreateVersion(station, full);
    } catch (e) {
      console.error("Failed to fetch station details", e);
      onCreateVersion(station, versionStation);
    }
  };

  const detailColumns: TableColumn<Station>[] = useMemo(
    () => [
      {
        key: "name",
        header: "NAME",
        accessor: "name",
        render: (_value, row) => <span className="font-medium">{row.name}</span>,
      },
      {
        key: "version",
        header: "VERSION",
        accessor: (row) => row.version,
        render: (_value, row) => {
          const isChildRow = !!(row as Station & { parent_buddy_id?: string }).parent_buddy_id;
          const versionLabel = isChildRow ? `v${row.version}` : `v${row.version} (Parent)`;
          return (
            <span className="inline-block px-1.5 py-0.5 text-xs rounded border border-gray-200 bg-gray-50 text-gray-700 font-mono">
              {versionLabel}
            </span>
          );
        },
      },
      {
        key: "created_at",
        header: "CREATED",
        accessor: "created_at",
        render: (_value, row) =>
          row.created_at ? new Date(row.created_at).toLocaleDateString() : "Unknown",
      },
      {
        key: "sort_order",
        header: "ORDER",
        accessor: (row) => (row as Station & { sort_order?: number }).sort_order,
        align: "center",
        render: (_value, row) => (
          <span className="inline-block px-2 py-1 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 border border-gray-200 min-w-[32px] text-center">
            {(row as Station & { sort_order?: number }).sort_order !== undefined
              ? (row as Station & { sort_order?: number }).sort_order
              : "-"}
          </span>
        ),
      },
      {
        key: "status",
        header: "STATUS",
        accessor: (row) => (row.is_active ? "Active" : "Inactive"),
        render: (_value, row) => (
          <Badge
            variant="outline"
            className={`text-xs font-medium ${
              row.is_active
                ? "bg-green-100 text-green-800 border-green-300"
                : "bg-gray-100 text-gray-600 border-gray-300"
            }`}
          >
            {row.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "ACTIONS",
        accessor: (row) => row.buddy_id,
        align: "center",
        render: (_value, row) => (
          <div className="flex items-center justify-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewStation(row)}
                    className="h-8 w-8 p-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasEditAccess && row.is_active && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditAsNewVersion(row)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit as new version</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {hasEditAccess && !row.is_active && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          await toggleStationVersion(row.buddy_id, true);
                          onStationsUpdated?.();
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Power className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Activate version</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {hasManageAccess && (row as Station & { parent_buddy_id?: string }).parent_buddy_id && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteStation(row)}
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete version</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ),
      },
    ],
    [
      hasEditAccess,
      hasManageAccess,
      onViewStation,
      onDeleteStation,
      onStationsUpdated,
      handleEditAsNewVersion,
    ]
  );

  const stationName = station?.name || "Station";

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Page header - same pattern as Users / other pages: single row, no breadcrumbs */}
      <div className="flex items-center justify-between px-8 py-4 border-b bg-white gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <CircleChevronLeft className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Back to Stations</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <h1 className="text-xl font-semibold text-gray-900 truncate">
            {stationName}
          </h1>
          <span className="text-sm text-muted-foreground shrink-0">Station Details</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasEditAccess && onCreateVersion && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateVersion}
                    className="h-8 px-3 shrink-0"
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    New Version
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Create a new version from active version</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {hasManageAccess && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDeleteStation(station)}
                    className="h-8 px-3 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 hover:border-red-400 shrink-0"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Delete
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete entire station</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Content - same padding as list view */}
      <div className="flex-1 flex flex-col min-h-0 px-8 pt-6 pb-2">
        <div className="flex-1 min-h-0 flex flex-col">
          <DataTable<Station>
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 200px)"
            columns={detailColumns}
            data={versions}
            emptyMessage="No versions."
            stickyHeader
            showActions={false}
          />
        </div>
      </div>
    </div>
  );
};

export default StationDetailsPanel;
